import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[95] bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, style, isBottomSheetOnMobile = true, ...props }, forwardedRef) => {
  const closeBtnRef = React.useRef(null);
  const startY = React.useRef(0);
  const isDragging = React.useRef(false);
  const [dragY, setDragY] = React.useState(0);
  const [isClosing, setIsClosing] = React.useState(false);
  const contentRef = React.useRef(null);

  // Merge del ref externo con el ref local
  const mergedRef = React.useCallback((node) => {
    contentRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  React.useEffect(() => {
    if (!isBottomSheetOnMobile) return;

    // Al enfocar un input dentro del modal, hacer scroll suave interno sin afectar la ventana principal
    const handleFocusIn = (e) => {
      const el = contentRef.current;
      if (!el || !el.contains(e.target)) return;
      if (typeof e.target?.scrollIntoView === 'function') {
        e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };

    // Ajustar padding-bottom dinámicamente a la altura del teclado activo
    const vv = window.visualViewport;
    const handleViewportChange = () => {
      const el = contentRef.current;
      if (!el || !vv) return;
      const keyboardH = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      if (keyboardH > 50) {
        el.style.paddingBottom = `${keyboardH + 16}px`;
      } else {
        el.style.paddingBottom = '';
      }
    };

    window.addEventListener('focusin', handleFocusIn);
    if (vv) {
      vv.addEventListener('resize', handleViewportChange);
      vv.addEventListener('scroll', handleViewportChange);
    }

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      if (vv) {
        vv.removeEventListener('resize', handleViewportChange);
        vv.removeEventListener('scroll', handleViewportChange);
      }
    };
  }, [isBottomSheetOnMobile]);

  const isFromHandle = React.useRef(false);

  const handleTouchStart = (e, fromHandle = false) => {
    if (!isBottomSheetOnMobile) return;
    isFromHandle.current = fromHandle;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (diff > 0 && (isFromHandle.current || isAtTop)) {
      setDragY(diff);
    } else if (diff < 0) {
      isDragging.current = false;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    // Si es desde la barrita: 75px. Si es desde el cuerpo: 190px (evita cierres accidentales)
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragY > threshold) {
      setIsClosing(true);
      setTimeout(() => {
        closeBtnRef.current?.click();
        setIsClosing(false);
        setDragY(0);
      }, 220);
    } else {
      setDragY(0);
    }
    startY.current = 0;
    isFromHandle.current = false;
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={mergedRef}
        onTouchStart={isBottomSheetOnMobile ? (e) => handleTouchStart(e, false) : undefined}
        onTouchMove={isBottomSheetOnMobile ? handleTouchMove : undefined}
        onTouchEnd={isBottomSheetOnMobile ? handleTouchEnd : undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          transform: isClosing
            ? 'translateY(100%)'
            : (dragY > 0 ? `translate3d(0, ${dragY}px, 0)` : undefined),
          transition: isDragging.current ? 'none' : 'transform 0.26s cubic-bezier(0.16, 1, 0.3, 1)',
          ...style
        }}
        className={cn(
          "fixed z-[100] grid gap-3.5 border bg-background shadow-2xl overflow-y-auto overflow-x-hidden break-words will-change-transform",
          isBottomSheetOnMobile
            ? "bottom-0 left-0 right-0 top-auto w-full max-w-lg mx-auto rounded-b-none rounded-t-[2.5rem] p-4 sm:p-6 pb-8 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2.5rem)] sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:bottom-auto sm:w-[92vw] sm:max-w-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom sm:data-[state=closed]:animate-out sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]"
            : "left-[50%] top-[50%] z-[100] w-[92vw] max-w-lg translate-x-[-50%] translate-y-[-50%] p-3.5 sm:p-6 rounded-3xl max-h-[88dvh] data-[state=open]:animate-in data-[state=closed]:animate-out",
          className
        )}
        {...props}>
        {/* Handle superior interactivo para pull-down en móvil */}
        {isBottomSheetOnMobile && (
          <div 
            onTouchStart={(e) => handleTouchStart(e, true)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'none' }}
            className="sm:hidden flex justify-center pt-1 pb-3 -mt-2 -mx-4 cursor-grab active:cursor-grabbing select-none"
          >
            <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
          </div>
        )}
        {children}
        <DialogPrimitive.Close
          ref={closeBtnRef}
          className="absolute right-3.5 top-3.5 rounded-full p-1.5 bg-slate-100/90 hover:bg-slate-200 text-slate-500 hover:text-slate-900 opacity-90 transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-20">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
